import React, { useEffect, useState, useContext } from 'react'
import { useInView } from 'react-intersection-observer'
import dlv from 'dlv'

import { Box, Flex, Link } from 'system'
import { Subheading } from './Subheading'
import { Dot } from './Dot'

const InPageNavigationContext = React.createContext()

const InPageNavigationProvider = ({ children }) => {
  const [currSection, setCurrSection] = useState(null)

  return (
    <InPageNavigationContext.Provider value={{ currSection, setCurrSection }}>
      {children}
    </InPageNavigationContext.Provider>
  )
}

const Items = ({ items = [], depth = 0, ...props }) => (
  <Box as="ul" {...props}>
    {items.map(item => (
      <Item
        href={dlv(item, 'url')}
        name={dlv(item, 'title')}
        key={dlv(item, 'url')}
      >
        {depth < 1 && (
          <Items
            items={dlv(item, 'items')}
            depth={depth + 1}
            pl={depth + 2}
            nested={true}
          />
        )}
      </Item>
    ))}
  </Box>
)

const Item = ({ href, name, children, ...props }) => {
  const [ref, inView] = useInView()
  const { currSection, setCurrSection } = useContext(InPageNavigationContext)
  useEffect(() => {
    ref(document.querySelector(href))
  }, [ref, href])
  useEffect(() => {
    if (inView) setCurrSection(href)
  }, [inView, href, setCurrSection])

  const isActive = currSection === href

  return (
    <Box
      as="li"
      position="relative"
      color={isActive ? 'purple.50' : 'gray.50'}
      {...props}
    >
      <Flex alignItems="center" mbScale="t">
        {currSection === href && (
          <Dot
            color="purple.50"
            size="6px"
            position="absolute"
            left={0}
            css={`
              transform: translateX(calc(-100% - 0.75rem));
            `}
          />
        )}
        <Link
          href={href}
          display="inline-block"
          fontWeight={isActive ? 'semibold' : 'normal'}
          fontSizeScale="s"
          css={`
            transition: color ${p => p.theme.transition};

            &:hover,
            &:focus {
              color: ${p => p.theme.colors.purple[40]};
            }
          `}
        >
          {name}
        </Link>
      </Flex>
      {children}
    </Box>
  )
}

export const InPageNavigation = ({ tableOfContents, ...props }) => (
  <InPageNavigationProvider>
    {Object.keys(tableOfContents).length > 0 && (
      <Box
        as="nav"
        color="gray.30"
        pr={2}
        display={['none', null, null, 'block']}
        {...props}
      >
        <Subheading mbScale="t+">Contents</Subheading>
        <Items items={dlv(tableOfContents, 'items')} />
      </Box>
    )}
  </InPageNavigationProvider>
)
